import { useState, useEffect, useLayoutEffect, useMemo } from 'react';

import api from '../../../apis';

import FormInput from '../../general/form-control/form-item/input';
import { useNavigate } from 'react-router-dom';
import property from '../../../constants/property';
import config from '../../../config';
import { Button, Divider } from 'antd';
import { setLogout } from '../../../hooks/handleUserLocalStorage';

import { showMessageModal, showSuccessMessageModal } from '../../../components/utils';
const manualTransferType = property.manualTransferType;


const ChangePasswordForm = ({
    isForceChangePassword = false,
    onSuccess = () => {},
    type = 'password', // 'password' = change password, 'trading_pin' = change trading pin
    skipLogout = false,
   // displayLogoutButton =false
}) => {
    const navigate = useNavigate();
    // const [cookies, setCookie,removeCookie] = useCookies(['user']);

    const isTradingPin = type === 'trading_pin';
    const label = isTradingPin ? 'Trading Pin' : 'Password';

    //for trading pin, avoid type="password" so Chrome doesn't trigger data-breach popup; mask with CSS instead
    const supportsTextSecurity =
        typeof CSS !== 'undefined' && CSS.supports('-webkit-text-security', 'disc');
    const secretInputProps: any =
        isTradingPin && supportsTextSecurity
            ? {
                  secureText: true,
                  autoComplete: 'off'
              }
            : { type: 'password' };

    const oldKey = `old_password`;
    const newKey = `new_password`;
    const reNewKey = `re_new_password`;

    const [loading, setLoading] = useState(false);
    const [inputs, setInputs] = useState<any>({});
    const [errors, setErrors] = useState<any>({});

    //validation settings
    const [rules, setRules] = useState<any>({
        [oldKey]: {
            required: true,
            error_message: `Please input current ${label.toLowerCase()}`
        },
        [newKey]: {
            required: true,
            error_message: `Please input new ${label.toLowerCase()}`
        },
        [reNewKey]: {
            required: true,
            same_value: newKey,
            same_value_error_message:
                `New ${label.toLowerCase()} and confirm ${label.toLowerCase()} not match`,
            error_message: `Please input confirm ${label.toLowerCase()}`
        }
    });

    const [passwordRulesText, setPasswordRulesText] = useState<any>(null);

    const buildFieldRules = (r: any) => {
        let patterns: any[] = [];
        let rulesText: any[] = [];

        if (r.min && r.max) {
            patterns.push({ pattern: new RegExp(`^.{${r.min},${r.max}}$`), message: `${r.min}-${r.max} characters` });
            rulesText.push(<div>{r.min}-{r.max} characters</div>);
        }

        if (r.alphabet) {
            if (r.sensitive) {
                patterns.push({ pattern: /[A-Z]/, message: 'At least one Alphabet (Uppercase)' });
                patterns.push({ pattern: /[a-z]/, message: 'At least one Alphabet (Lowercase)' });
                rulesText.push(<div>At least one Alphabet (Uppercase)</div>);
                rulesText.push(<div>At least one Alphabet (Lowercase)</div>);
            } else {
                patterns.push({ pattern: /[a-zA-Z]/, message: 'At least one Alphabet' });
            //    rulesText.push(<div>At least one Alphabet</div>);
            }
        } else if (r.alphabet === false) {
            patterns.push({ pattern: /^[^a-zA-Z]*$/, message: 'Alphabets are not allowed' });
           // rulesText.push(<div>Alphabets are not allowed</div>);
        }

        if (r.numeric === true) {
            patterns.push({ pattern: /\d/, message: 'At least one Number' });
            rulesText.push(<div>At least one Number</div>);
        } else if (r.numeric === false) {
            patterns.push({ pattern: /^\D*$/, message: 'Numbers are not allowed' });
          //  rulesText.push(<div>Numbers are not allowed</div>);
        }

        if (r.symbol === true) {
            patterns.push({ pattern: /[^a-zA-Z0-9\s]/, message: 'At least one Symbol' });
            rulesText.push(<div>At least one Symbol</div>);
        } else if (r.symbol === false) {
            patterns.push({ pattern: /^[a-zA-Z0-9]*$/, message: 'Symbols are not allowed' });
            //rulesText.push(<div>Symbols are not allowed</div>);
        } else if (r.symbol) {
            const escapedSymbols = r.symbol.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const symbolRegex = new RegExp(`[${escapedSymbols}]`);
            patterns.push({ pattern: symbolRegex, message: `At least one Symbol (${r.symbol})` });
            rulesText.push(<div>At least one Symbol ({r.symbol})</div>);
        }

        if (r.startWith === 'alphabet') {
            patterns.push({ pattern: /^[A-Za-z]/, message: 'Must start with an alphabet' });
            rulesText.push(<div>Must start with an alphabet</div>);
        } else if (r.startWith === 'numeric') {
            patterns.push({ pattern: /^[0-9]/, message: 'Must start with a number' });
            rulesText.push(<div>Must start with a number</div>);
        }

        patterns.push({ pattern: /^\S+$/, message: 'Spaces are not allowed' });

        return { patterns, rulesText };
    };

    useEffect(() => {
        const requestCredentialRules = async () => {
            const result = await api.userCredentialRules();
            if (!result?.rules) return;
            let target = isTradingPin ? result.rules.tradepin : result.rules.password;
            if (!target) return;

            //force use numeric
            if (isTradingPin && config.traderPinForceNumeric) {
                target = { ...target, alphabet: false, numeric: true, symbol: false, sensitive: false };
            }

            const { patterns, rulesText } = buildFieldRules(target);
            setRules((prev: any) => ({
                ...prev,
                [newKey]: {
                    ...prev[newKey],
                    patterns
                }
            }));
            setPasswordRulesText(
                <div>
                    {label} must meet ALL of the following requirements ONLY:
                    {rulesText}
                </div>
            );
        };
        requestCredentialRules();
    }, [isTradingPin]);

    const onInputChange = (id: string, newValue: string) => {
        const newInputs = { ...inputs, [id]: newValue };
        setInputs(newInputs);

        const { errors: newErrors } = formValidation(rules, newInputs);
        const filteredErrors: any = {};
        for (const k in newErrors) {
            if (newInputs[k]) {
                filteredErrors[k] = newErrors[k];
            }
        }
        setErrors(filteredErrors);
    };

    const formValidation = (rules: any, inputs: any) => {
        if (!rules) return { valid: true, errors: {} };

        let valid = true;
        let errors: any = {};

        //validate rule
        for (var key in rules) {
            if (rules.hasOwnProperty(key)) {
                let item = rules[key];
                if (item.required == true && !inputs[key]) {
                    let error = item.error_message || 'Required';
                    errors[key] = error;
                    valid = false;
                } else if (item.requireIfExist) {
                    //if specific input exist,current input become required
                    let requireIfExist = item.requireIfExist;
                    let relatedkey = requireIfExist.key;
                    let relatedValue = requireIfExist.value;

                    //match type start check error
                    if (
                        relatedkey &&
                        relatedValue &&
                        inputs[relatedkey] &&
                        inputs[relatedkey] == relatedValue
                    ) {
                        if (!inputs[key]) {
                            let error = item.error_message || 'Required';
                            errors[key] = error;
                            valid = false;
                        }
                    }
                } //match value with
                if (item.same_value && inputs[key]) {
                    let same_value = item.same_value;

                    if (inputs[key] != inputs[same_value]) {
                        let error = item.same_value_error_message || 'Required';
                        errors[key] = error;
                        valid = false;
                    }
                } else {
                }

                if (item.patterns && inputs[key] && !errors[key]) {
                    for (const p of item.patterns) {
                        if (!p.pattern.test(inputs[key])) {
                            errors[key] = p.message;
                            valid = false;
                            break;
                        }
                    }
                }

                // requireIfExist: { key: 'mode', value: 2 }
            }
        }

        return {
            valid,
            errors
        };
    };

    const handleSubmit = async (e: any) => {
        e.preventDefault();

    //    console.log('inputs', inputs);

        setLoading(true);
        let { valid, errors } = formValidation(rules, inputs);
        setErrors(errors);
       // console.log(errors);
        if (valid == false) {
            //scroll to first error
            if (Object.keys(errors).length > 0) {
                let firstKey = Object.keys(errors)[0];
                let element = document.getElementById(firstKey);
                element?.scrollIntoView();
            }
            setLoading(false);
        } else {
            setLoading(true);

            let result = await api.changePassword({...inputs, type: isTradingPin ? 1 : 0});
            setLoading(false);
           
            if (result.status == 0) {
                showSuccessMessageModal(`${label} changed successfully`);
                //if (props.setModelOpen) props.setModelOpen(false);

                if(isForceChangePassword == true && !skipLogout){
                    handleLogout();
                }

                if(onSuccess){
                    onSuccess();
                }
            } else if (result.status == 1) {
                let message = result?.message;

                if (message) {
                    if (typeof message === 'string') {
                        //alert(message);
                        showMessageModal(message);
                    } else {
                        let text = '';
                        for (var key in message) {
                            if (message.hasOwnProperty(key)) {
                                let sentence = message[key];
                                if (key == newKey) {
                                    sentence = sentence.replace(
                                        `"${newKey}"`,
                                        `New ${label.toLowerCase()}`
                                    );
                                }
                                text += sentence + '\n';
                            }
                        }
                        //alert(text);
                        showMessageModal(text);
                    }
                }
            }
           // console.log('changePassword', result);

        
            //submit to api
            // let contracts = handlePendingContracts(selectedRows);

            // if (contracts.length == 0) {
            //     alert('must select at least 1 item');
            // } else {
            //     let result = await api.submitMultiSettlement({
            //         bank: inputs.bank_list,
            //         contracts
            //     });
            //     console.log('result');
            //     console.log(result);

            //     if (result?.data?.fpx) {
            //         let fpx_url = result.data.fpx;
            //         window.open(fpx_url, '_blank', 'noopener,noreferrer');
            //         alert('Thank you, the form was submitted successfully');
            //         navigate(0);
            //         //requestGetAccountContractAPI(currentAccNo, inputs);
            //     }
            // }
        }

        // let result = await api.submitDepositManualBulk(currentAccNo, {
        //     pendingList
        // });
        // console.log('handleSubmit');
        // console.log(result);
        // if (result.status === 0) {
        //     alert('Thank you, the form was submitted successfully');
        //     //refresh page
        //     navigate(0);
        // } else {
        //     alert('submit failed');
        // }
    };

    
    const handleLogout = async () => {
        setLoading(true);
        let result = await api.userLogout();

        if (result == true) {
            // removeCookie('user', { path: '/' });
            setLogout();
            navigate('/login');
        }
        setLoading(false);
    };
   
   return (<>
        <form>
            {/* <div className="row ">
                <div className="col-xxxl-12 col-xl-12 col-lg-12 col-md-12 col-12">
                    <h5>Change Password</h5>
                </div>
            </div> */}
            <div className="row pt-3">
                <div className="col-xxxl-12 col-xl-12 col-lg-12 col-md-12 col-12">
                    <div>
                      

                        {
                            isForceChangePassword ?
                                <>For security reasons, you are required to change your {label.toLowerCase()}. Please update your {label.toLowerCase()} to continue accessing your account.</>
                                :
                                <>  Use this form to change your {label.toLowerCase()}. Once
                                changed, your new {label.toLowerCase()} will be in effect
                                next time you login</>
                        }
                    </div>
                </div>
            </div>
            <div className="row pt-2">
                <div className="col-xxxl-12 col-xl-12 col-lg-12 col-md-12 col-12">
                    <div className="form-input-container ">
                        <div className="label">Current {label}:</div>
                        <FormInput
                            id={oldKey}
                            {...secretInputProps}
                            value={inputs[oldKey] || ''}
                            onChange={({ target }: any) =>
                                onInputChange(target.id, target.value)
                            }
                            error={errors[oldKey]}
                        />
                    </div>
                </div>
            </div>
            <div className="row ">
                <div className="col-xxxl-12 col-xl-12 col-lg-12 col-md-12 col-12">
                    <div className="form-input-container ">
                        <div className="label">New {label}:</div>
                        <FormInput
                            id={newKey}
                            {...secretInputProps}
                            value={inputs[newKey] || ''}
                            onChange={({ target }: any) =>
                                onInputChange(target.id, target.value)
                            }
                            error={errors[newKey]}
                        />
                    </div>
                    {passwordRulesText}
                </div>
            </div>
            <div className="row ">
                <div className="col-xxxl-12 col-xl-12 col-lg-12 col-md-12 col-12">
                    <div className="form-input-container ">
                        <div className="label">Confirm {label}:</div>
                        <FormInput
                            id={reNewKey}
                            {...secretInputProps}
                            value={inputs[reNewKey] || ''}
                            onChange={({ target }: any) =>
                                onInputChange(target.id, target.value)
                            }
                            error={errors[reNewKey]}
                        />
                    </div>
                </div>
            </div>
            <Divider  />
            <div className='d-flex justify-content-between'>
                <div>
                    <Button type="primary" onClick={(e)=>handleSubmit(e)} className='btn-primary-custom btn-md' loading={loading}>
                        Change {label}
                    </Button>
                </div>
           
                <div>
                {
                    isForceChangePassword && (
                        <Button type="primary"   className=' btn-light btn-md' loading={loading} onClick={handleLogout}>
                           Cancel and Logout
                        </Button>
                    )
                }
                </div>
            </div>
        </form>
   </>)
};

export default ChangePasswordForm;
